import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import AgGridTable from "../../components/table/AgGridTable";
import WhiteTooltip from "../../components/WhiteTooltip";
import type { DotControl, DotLinkedPid, DotStatus } from "./types";
import type { DotEditSavePayload } from "./DoTEditDrawer";
import sparkLogo from "../../assets/Spark-text.svg";
import spark_dark from "../../assets/spark_dark.svg";
import cis_light from "../../assets/cis_light.svg";
import cis_dark from "../../assets/cis_dark.svg";
import nistLogo from "../../assets/nist.svg";
import nist_dark from "../../assets/nist_dark.svg";
import arista_assets from "../../assets/arista-assets.svg";
import arista_dark from "../../assets/arista-white.svg";
import cisco_assets from "../../assets/cisco-assets.svg";
import cisco_dark from "../../assets/cisco-white.svg";
import juniperLogo from "../../assets/juniper.svg";
import juniper_dark from "../../assets/juniper-white.svg";
import paloalto_assets from "../../assets/paloalto-assets.svg";
import paloalto_dark from "../../assets/paloalto-white.svg";
import ubuntu_white from "../../assets/ubuntu_white.svg";
import ubuntu_dark from "../../assets/ubuntu_dark.svg";
import windows_light from "../../assets/baseVersion/Windows_light.svg";
import windows_dark from "../../assets/baseVersion/Windows_dark.svg";
import debian from "../../assets/debian.svg";
import debian_dark from "../../assets/debian_dark.svg";
import redhat_light from "../../assets/redhat_light.svg";
import redhat_dark from "../../assets/redhat_dark.svg";
import huawei_light from "../../assets/huawei-ar21 1.svg";
import huawei_dark from "../../assets/huawei-dark.svg";
import mikrotik from "../../assets/mikrotik.svg";
import mikrotik_dark from "../../assets/mikrotik_dark.svg";
import edgecore_logo from "../../assets/baseVersion/edgecore-logo.svg";
import tplink_light from "../../assets/baseVersion/TPLINK_Logo_2 1.svg";
import tplink_dark from "../../assets/baseVersion/TPLINK_Logo_dark.svg";
import fortinet_img from "../../assets/fortinet.svg";
import fortinet_scan_dark from "../../assets/fortinet_scan_dark.svg";
import sophosLogo from "../../assets/sophos.svg";
import sophos_dark from "../../assets/sophos_dark.svg";
import apple_img from "../../assets/apple.svg";
import apple_dark from "../../assets/apple_dark.svg";
import dell_logo from "../../assets/baseVersion/dell-seeklogo 2.svg";
import syrotech_light from "../../assets/syrotech_light.svg";
import syrotech_dark from "../../assets/syrotech_dark.svg";

const ACCENT = "#4F46E5";
const COMMENTS_MAX = 500;

function frameworkLogo(framework: string, isDark: boolean): string | null {
  const key = (framework || "").toUpperCase();
  if (key.includes("CIS")) return isDark ? cis_dark : cis_light;
  if (key.includes("NIST")) return isDark ? nist_dark : nistLogo;
  // Same wordmark used in SPARK framework recommendations tables
  return isDark ? spark_dark : sparkLogo;
}

function titleCaseType(value: string): string {
  const raw = (value || "").trim();
  if (!raw || raw === "—") return "—";
  return raw
    .split(/[\s/_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function vendorLogoSrc(
  vendor: string | undefined,
  isDark: boolean
): { src: string; alt: string; width?: number; height?: number; filter?: string } | null {
  const key = (vendor || "").trim().toLowerCase();
  if (!key || key === "—") return null;
  if (key === "cisco")
    return { src: isDark ? cisco_dark : cisco_assets, alt: "Cisco", width: 60, height: 20 };
  if (key === "juniper")
    return { src: isDark ? juniper_dark : juniperLogo, alt: "Juniper", width: 60, height: 20 };
  if (key === "palo alto" || key === "paloalto")
    return {
      src: isDark ? paloalto_dark : paloalto_assets,
      alt: "Palo Alto",
      width: 60,
      height: 20,
    };
  if (key === "arista")
    return { src: isDark ? arista_dark : arista_assets, alt: "Arista", width: 60, height: 20 };
  if (key === "fortinet")
    return {
      src: isDark ? fortinet_scan_dark : fortinet_img,
      alt: "Fortinet",
      width: 50,
      height: 24,
    };
  if (key === "ubuntu")
    return { src: isDark ? ubuntu_dark : ubuntu_white, alt: "Ubuntu", width: 60, height: 22 };
  if (key === "microsoft" || key === "windows")
    return {
      src: isDark ? windows_dark : windows_light,
      alt: "Microsoft",
      width: 65,
      height: 22,
    };
  if (key === "redhat" || key === "rhel" || key === "red hat")
    return { src: isDark ? redhat_dark : redhat_light, alt: "RedHat", width: 60, height: 22 };
  if (key === "debian")
    return { src: isDark ? debian_dark : debian, alt: "Debian", width: 60, height: 22 };
  if (key === "huawei")
    return { src: isDark ? huawei_dark : huawei_light, alt: "Huawei", width: 60, height: 22 };
  if (key === "mikrotik")
    return { src: isDark ? mikrotik_dark : mikrotik, alt: "Mikrotik", width: 90, height: 35 };
  if (key === "edgecore")
    return {
      src: edgecore_logo,
      alt: "Edgecore",
      width: 80,
      height: 30,
      filter: isDark ? "brightness(0) invert(1)" : "none",
    };
  if (key === "tp-link" || key === "tplink")
    return { src: isDark ? tplink_dark : tplink_light, alt: "TP-Link", width: 80, height: 30 };
  if (key === "dell")
    return { src: dell_logo, alt: "Dell", width: 40, height: 40 };
  if (key === "syrotech")
    return {
      src: isDark ? syrotech_dark : syrotech_light,
      alt: "Syrotech",
      width: 90,
      height: 32,
    };
  if (key === "sophos")
    return { src: isDark ? sophos_dark : sophosLogo, alt: "Sophos", width: 60, height: 22 };
  if (key === "apple" || key === "macos")
    return { src: isDark ? apple_dark : apple_img, alt: "Apple", width: 28, height: 28 };
  return null;
}

interface Props {
  open: boolean;
  control: DotControl | null;
  onClose: () => void;
  onSave?: (payload: DotEditSavePayload) => Promise<void> | void;
  saving?: boolean;
  /** Phase 1: only Admins can override / save */
  canEdit?: boolean;
}

function ComplianceRing({
  value,
  size = 30,
  showLabel = true,
  labelSize = 12,
}: {
  value: number;
  size?: number;
  showLabel?: boolean;
  labelSize?: number;
}) {
  const pct = Math.min(100, Math.max(0, Math.round(value)));
  const color = pct >= 75 ? "green" : pct >= 50 ? "orange" : "red";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ position: "relative", width: size, height: size }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={5}
          sx={{ color: "#c5c5d2" }}
        />
        <CircularProgress
          variant="determinate"
          value={pct}
          size={size}
          thickness={5}
          sx={{
            color,
            position: "absolute",
            left: 0,
          }}
        />
      </Box>
      {showLabel ? (
        <Typography sx={{ fontWeight: 500, fontSize: labelSize }}>
          {pct}%
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Match CIS/NIST/PCI drawers:
 * - Recommendations tab = unique checks (norm_id)
 * - Assets tab = unique devices with overall rec counts for this control
 * - Override section above tabs (inline — no second navigation)
 */
const DoTPidDrilldownDrawer: React.FC<Props> = ({
  open,
  control,
  onClose,
  onSave,
  saving = false,
  canEdit = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [tab, setTab] = useState(0);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [actualResultOpen, setActualResultOpen] = useState(true);
  const [status, setStatus] = useState<DotStatus>(null);
  const [comments, setComments] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    if (open) setTab(0);
  }, [open]);

  useEffect(() => {
    if (!control) return;
    setStatus(control.status);
    setComments(control.comments || "");
    setOverrideReason(control.override_reason || "");
    // Only Admins can open the override editor
    setOverrideOpen(canEdit && Boolean(control.override));
    // Collapse scan results when override is active; expand when not
    setActualResultOpen(!control.override);
  }, [control, open, canEdit]);

  const pids: DotLinkedPid[] = control?.linked_pids || [];
  const muted = isDark ? "rgba(255,255,255,0.55)" : "#6B7280";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)";
  const surface = isDark ? "#191C24" : "#FFFFFF";
  const guidelineBg = isDark ? "rgba(59,130,246,0.12)" : "#EFF6FF";
  const guidelineBorder = isDark ? "rgba(59,130,246,0.28)" : "#BFDBFE";
  const requirementBg = isDark ? "rgba(99,102,241,0.14)" : "#EEF2FF";
  const requirementBorder = isDark ? "rgba(99,102,241,0.3)" : "#C7D2FE";
  const overrideBg = isDark ? "rgba(245,158,11,0.1)" : "#FFFBEB";
  const overrideBorder = isDark ? "rgba(245,158,11,0.35)" : "#FDE68A";

  const sectionCrumb = control?.section
    ? `${control.section}${
        control.section_heading ? ` ${control.section_heading}` : ""
      }`
    : null;
  const subsectionCrumb = control?.subsection
    ? `${control.subsection}${
        control.subsection_heading ? ` ${control.subsection_heading}` : ""
      }`
    : null;
  const guidelineText =
    (control?.description || "").trim() ||
    (control?.title || "").trim() ||
    "";
  const requirementText = (control?.note || "").trim();

  const differsFromAuto =
    status != null && status !== (control?.auto_status ?? null);
  const needsReason = overrideOpen && differsFromAuto;

  /** Unique recommendations (by norm_id / pid) — same grain as ControlDetails */
  const recommendationRows = useMemo(() => {
    const byKey = new Map<
      string,
      {
        id: string;
        pid: string;
        name: string;
        framework: string;
        asset_count: number;
        pass: number;
        total: number;
        percentage: number;
      }
    >();
    for (const p of pids) {
      const key = p.norm_id || p.pid;
      const devices = p.devices || [];
      let pass = 0;
      for (const d of devices) {
        if (d.status === "Pass") pass += 1;
      }
      const total = devices.length;
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, {
          id: key,
          pid: p.pid,
          name: p.title || p.pid,
          framework: p.framework || "SPARK",
          asset_count: total,
          pass,
          total,
          percentage: total ? Math.round((pass / total) * 100) : 0,
        });
      } else {
        existing.asset_count += total;
        existing.pass += pass;
        existing.total += total;
        existing.percentage = existing.total
          ? Math.round((existing.pass / existing.total) * 100)
          : 0;
      }
    }
    return Array.from(byKey.values());
  }, [pids]);

  const assetRows = useMemo(() => {
    const byDevice = new Map<
      string,
      {
        id: string;
        device_name: string;
        device_type: string;
        vendor: string;
        model: string;
        recommendations: number;
        passed_recommendations: number;
        percentage: number;
      }
    >();
    for (const p of pids) {
      for (const d of p.devices || []) {
        const id = d.device_id || d.device_name;
        const vendor = d.vendor || p.vendor || "—";
        const model = d.model || p.model || "—";
        const cur = byDevice.get(id) || {
          id,
          device_name: d.device_name,
          device_type: d.device_type || "—",
          vendor,
          model,
          recommendations: 0,
          passed_recommendations: 0,
          percentage: 0,
        };
        if ((!cur.vendor || cur.vendor === "—") && vendor !== "—") {
          cur.vendor = vendor;
        }
        if ((!cur.model || cur.model === "—") && model !== "—") {
          cur.model = model;
        }
        cur.recommendations += 1;
        if (d.status === "Pass") cur.passed_recommendations += 1;
        cur.percentage = cur.recommendations
          ? Math.round(
              (cur.passed_recommendations / cur.recommendations) * 100
            )
          : 0;
        byDevice.set(id, cur);
      }
    }
    return Array.from(byDevice.values());
  }, [pids]);

  const complianceStats = useMemo(() => {
    let pass = 0;
    let total = 0;
    for (const p of pids) {
      for (const d of p.devices || []) {
        total += 1;
        if (d.status === "Pass") pass += 1;
      }
    }
    return {
      pass,
      total,
      pct: total ? Math.round((pass / total) * 100) : 0,
    };
  }, [pids]);
  const compliancePct = complianceStats.pct;

  const recommendationStats = useMemo(() => {
    const total = recommendationRows.length;
    const compliant = recommendationRows.filter((r) => r.percentage === 100)
      .length;
    return {
      total,
      compliant,
      needAction: total - compliant,
    };
  }, [recommendationRows]);

  const assetStats = useMemo(() => {
    const total = assetRows.length;
    const compliant = assetRows.filter((r) => r.percentage === 100).length;
    return {
      total,
      compliant,
      needAction: total - compliant,
    };
  }, [assetRows]);

  const recommendationCols: ColDef[] = useMemo(
    () => [
      {
        headerName: "Benchmark",
        field: "framework",
        minWidth: 130,
        maxWidth: 170,
        filter: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
        cellRenderer: (p: ICellRendererParams) => {
          const label = String(p.value || "SPARK");
          const logo = frameworkLogo(label, isDark);
          const isNist = label.toUpperCase().includes("NIST");
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                pt: 0.5,
              }}
            >
              {logo ? (
                <Box
                  component="img"
                  src={logo}
                  alt={label}
                  sx={{
                    height: isNist ? 12 : 24,
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {label}
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        headerName: "Recommendation",
        field: "name",
        flex: 1.8,
        minWidth: 240,
      },
      {
        headerName: "Assets",
        field: "asset_count",
        minWidth: 100,
        width: 110,
        flex: 0,
        suppressSizeToFit: true,
      },
      {
        headerName: "Compliance",
        field: "percentage",
        minWidth: 120,
        width: 130,
        flex: 0,
        suppressSizeToFit: true,
        cellRenderer: (p: ICellRendererParams) => (
          <ComplianceRing value={Number(p.value) || 0} />
        ),
      },
    ],
    [isDark]
  );

  const assetCols: ColDef[] = useMemo(
    () => [
      {
        headerName: "Asset",
        field: "device_name",
        flex: 1,
        minWidth: 160,
        filter: true,
      },
      {
        headerName: "Vendor",
        field: "vendor",
        minWidth: 120,
        maxWidth: 170,
        filter: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
        },
        cellRenderer: (p: ICellRendererParams) => {
          const logo = vendorLogoSrc(p.value, isDark);
          if (logo) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Box
                  component="img"
                  src={logo.src}
                  alt={logo.alt}
                  title={String(p.value || logo.alt)}
                  sx={{
                    width: logo.width,
                    height: logo.height,
                    objectFit: "contain",
                    filter: logo.filter || "none",
                  }}
                />
              </Box>
            );
          }
          return (
            <Typography
              title={String(p.value || "")}
              sx={{
                fontSize: 13,
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {p.value || "—"}
            </Typography>
          );
        },
      },
      {
        headerName: "OS",
        field: "model",
        minWidth: 110,
        maxWidth: 150,
        filter: true,
      },
      {
        headerName: "Type",
        field: "device_type",
        minWidth: 120,
        width: 130,
        flex: 0,
        suppressSizeToFit: true,
        valueFormatter: (p) => titleCaseType(String(p.value || "")),
        tooltipValueGetter: (p) => titleCaseType(String(p.value || "")),
        cellStyle: {
          whiteSpace: "nowrap",
          overflow: "visible",
          textOverflow: "clip",
        },
      },
      {
        headerName: "Recommendations",
        field: "recommendations",
        minWidth: 150,
        width: 160,
        flex: 0,
        suppressSizeToFit: true,
      },
      {
        headerName: "Passed",
        field: "passed_recommendations",
        minWidth: 100,
        width: 110,
        flex: 0,
        suppressSizeToFit: true,
      },
      {
        headerName: "Compliance",
        field: "percentage",
        minWidth: 120,
        width: 130,
        flex: 0,
        suppressSizeToFit: true,
        cellRenderer: (p: ICellRendererParams) => (
          <ComplianceRing value={Number(p.value) || 0} />
        ),
      },
    ],
    [isDark]
  );

  if (!control) return null;

  const handleEnableOverride = () => {
    if (!canEdit) return;
    setStatus(control.status ?? control.auto_status);
    setOverrideReason(control.override_reason || "");
    setComments(control.comments || "");
    setOverrideOpen(true);
  };

  const handleSaveOverride = async () => {
    if (!canEdit || !onSave) return;
    if (needsReason && !(overrideReason || "").trim()) return;
    await onSave({
      control_id: control.id,
      status,
      source: "SPARK",
      comments,
      override: differsFromAuto || control.override,
      override_reason: differsFromAuto ? overrideReason.trim() : "",
    });
  };

  const handleResetAuto = async () => {
    if (!canEdit || !onSave) return;
    await onSave({
      control_id: control.id,
      status: control.auto_status,
      source: "SPARK",
      comments,
      reset_to_auto: true,
    });
  };

  const headerStyles = {
    "& .ag-header-cell-label": { overflow: "visible" },
    "& .ag-header-cell-text": {
      overflow: "visible !important",
      textOverflow: "clip !important",
      whiteSpace: "nowrap",
    },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: "55%",
          p: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          bgcolor: isDark ? "#191C24" : "#fff",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          px: 2,
          pt: 2,
          pb: 1.5,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            flexShrink: 0,
            bgcolor: isDark ? "rgba(79,70,229,0.22)" : "#EEF2FF",
            color: ACCENT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
          <Typography
            sx={{
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1.35,
              color: isDark ? "#fff" : "#111827",
            }}
          >
            {control.id} {control.title || control.description}
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontSize: 13,
              color: muted,
              lineHeight: 1.4,
            }}
          >
            {["DoT / MBSS", sectionCrumb, subsectionCrumb]
              .filter(Boolean)
              .join(" › ")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: muted, mt: -0.5, flexShrink: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, pb: 1 }}>
        {guidelineText ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.75,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: guidelineBg,
              border: `1px solid ${guidelineBorder}`,
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 20,
                color: isDark ? "#93C5FD" : "#2563EB",
                mt: 0.15,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: isDark ? "#93C5FD" : "#1D4ED8",
                  mb: 0.5,
                }}
              >
                Guideline
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: isDark ? "rgba(255,255,255,0.85)" : "#1F2937",
                  whiteSpace: "pre-wrap",
                }}
              >
                {guidelineText}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {requirementText ? (
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.75,
              mb: 1.5,
              borderRadius: 2,
              bgcolor: requirementBg,
              border: `1px solid ${requirementBorder}`,
            }}
          >
            <FormatQuoteRoundedIcon
              sx={{
                fontSize: 22,
                color: isDark ? "#A5B4FC" : "#4F46E5",
                mt: 0.1,
                flexShrink: 0,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: isDark ? "#A5B4FC" : "#4338CA",
                  mb: 0.5,
                }}
              >
                Requirement
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  fontStyle: "italic",
                  color: isDark ? "rgba(255,255,255,0.85)" : "#312E81",
                  whiteSpace: "pre-wrap",
                }}
              >
                {requirementText}
              </Typography>
            </Box>
          </Box>
        ) : null}

        {/* Override section — above recommendations */}
        <Box
          sx={{
            mb: 2,
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${overrideBorder}`,
            bgcolor: overrideBg,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              mb: overrideOpen ? 1.5 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditNoteRoundedIcon sx={{ fontSize: 20, color: "#D97706" }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                Override
              </Typography>
              {control.override ? (
                <Chip size="small" label="Active" color="warning" />
              ) : null}
            </Box>
            {canEdit && !overrideOpen ? (
              <Button
                size="small"
                variant="outlined"
                onClick={handleEnableOverride}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  borderColor: "#F59E0B",
                  color: "#B45309",
                }}
              >
                Enable override
              </Button>
            ) : null}
          </Box>

          {!canEdit ? (
            <Typography sx={{ fontSize: 13, color: muted, mt: 1 }}>
              Auto status:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "inherit" }}>
                {control.auto_status || "—"}
              </Box>
              {" · "}
              Recorded:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {control.status || "—"}
              </Box>
              <Box component="span" sx={{ display: "block", mt: 0.75 }}>
                Only Admins can update this control.
              </Box>
            </Typography>
          ) : !overrideOpen ? (
            <Typography sx={{ fontSize: 13, color: muted, mt: 1 }}>
              Auto status:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "inherit" }}>
                {control.auto_status || "—"}
              </Box>
              {" · "}
              Recorded:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {control.status || "—"}
              </Box>
            </Typography>
          ) : (
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  color: muted,
                  mb: 1,
                }}
              >
                Auto from SPARK PIDs:{" "}
                <strong>{control.auto_status || "—"}</strong>
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  mb: 0.75,
                  color: isDark ? "rgba(255,255,255,0.9)" : "#374151",
                }}
              >
                Compliance Status
              </Typography>
              <RadioGroup
                row
                value={status ?? ""}
                onChange={(_, v) => setStatus((v || null) as DotStatus)}
                sx={{
                  mb: 1.5,
                  "& .MuiRadio-root.Mui-checked": { color: ACCENT },
                }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio size="small" />}
                  label="Yes"
                />
                <FormControlLabel
                  value="No"
                  control={<Radio size="small" />}
                  label="No"
                />
              </RadioGroup>

              {differsFromAuto ? (
                <TextField
                  label="Override reason"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  fullWidth
                  required
                  multiline
                  minRows={2}
                  size="small"
                  sx={{ mb: 1.5 }}
                  error={needsReason && !(overrideReason || "").trim()}
                  helperText="Required when status differs from auto"
                />
              ) : null}

              <TextField
                label="Comments / Artefact notes"
                value={comments}
                onChange={(e) =>
                  setComments(e.target.value.slice(0, COMMENTS_MAX))
                }
                fullWidth
                multiline
                minRows={2}
                size="small"
                placeholder="Optional"
                helperText={`${comments.length} / ${COMMENTS_MAX}`}
                FormHelperTextProps={{
                  sx: { textAlign: "right", mx: 0 },
                }}
                sx={{ mb: 1.5 }}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                {(control.override || differsFromAuto) && (
                  <Button
                    onClick={handleResetAuto}
                    disabled={saving}
                    color="inherit"
                    sx={{ textTransform: "none", fontWeight: 600, mr: "auto" }}
                  >
                    Reset to auto
                  </Button>
                )}
                {!control.override && (
                  <Button
                    onClick={() => {
                      setOverrideOpen(false);
                      setStatus(control.status);
                      setOverrideReason(control.override_reason || "");
                      setComments(control.comments || "");
                    }}
                    disabled={saving}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      borderColor: border,
                      color: isDark ? "#E5E7EB" : "#374151",
                      bgcolor: surface,
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="contained"
                  onClick={handleSaveOverride}
                  disabled={
                    saving ||
                    !canEdit ||
                    !onSave ||
                    (needsReason && !(overrideReason || "").trim())
                  }
                  startIcon={<SaveOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: ACCENT,
                    "&:hover": { bgcolor: "#4338CA" },
                  }}
                >
                  {saving ? "Saving…" : "Save"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Actual result — collapsed by default when override is active */}
        <Box sx={{ mt: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: control.override ? 1 : 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: isDark ? "#E5E7EB" : "#111827",
              }}
            >
              Actual result
            </Typography>
            <WhiteTooltip
              title={
                <Box sx={{ p: 0.25, maxWidth: 300 }}>
                  {control.override ? (
                    <>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}
                      >
                        Override is active
                      </Typography>
                      <Typography sx={{ fontSize: 12, lineHeight: 1.45 }}>
                        The manually overridden compliance status is what will
                        be considered in the report — not the SPARK scan result
                        below.
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, lineHeight: 1.45, mt: 0.75 }}
                      >
                        Auto from PIDs:{" "}
                        <strong>{control.auto_status || "—"}</strong>
                        {" · "}
                        Recorded: <strong>{control.status || "—"}</strong>
                      </Typography>
                      {(control.override_reason || "").trim() ? (
                        <Typography
                          sx={{ fontSize: 12, lineHeight: 1.45, mt: 0.75 }}
                        >
                          Reason: {control.override_reason}
                        </Typography>
                      ) : null}
                      <Typography
                        sx={{ fontSize: 12, lineHeight: 1.45, mt: 0.75 }}
                      >
                        Expand to review scan recommendations and assets. Use
                        Reset to auto to drive status from scan again.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}
                      >
                        Driven by SPARK scan
                      </Typography>
                      <Typography sx={{ fontSize: 12, lineHeight: 1.45 }}>
                        Compliance%, recommendations, and assets below come from
                        linked SPARK PID scans and are what will be considered in
                        the report.
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, lineHeight: 1.45, mt: 0.75 }}
                      >
                        Auto status:{" "}
                        <strong>{control.auto_status || "—"}</strong>
                        {" · "}
                        Recorded: <strong>{control.status || "—"}</strong>
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, lineHeight: 1.45, mt: 0.75 }}
                      >
                        Enable Override above if you need to set compliance
                        status manually. When override is active, override
                        results are used in the report instead.
                      </Typography>
                    </>
                  )}
                </Box>
              }
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: control.override
                    ? isDark
                      ? "#FBBF24"
                      : "#D97706"
                    : muted,
                  cursor: "help",
                }}
              />
            </WhiteTooltip>
            {control.override ? (
              <>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  onClick={() => setActualResultOpen((v) => !v)}
                  endIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        fontSize: 18,
                        transform: actualResultOpen
                          ? "rotate(180deg)"
                          : "none",
                        transition: "transform 0.2s",
                      }}
                    />
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    color: muted,
                    minWidth: 0,
                  }}
                >
                  {actualResultOpen ? "Collapse" : "Expand"}
                </Button>
              </>
            ) : null}
          </Box>

          {control.override ? (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
                p: 1.25,
                mb: 1,
                borderRadius: 1.5,
                bgcolor: overrideBg,
                border: `1px solid ${overrideBorder}`,
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: isDark ? "#FBBF24" : "#D97706",
                  mt: 0.1,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: 1.5,
                  color: isDark ? "rgba(255,255,255,0.85)" : "#92400E",
                }}
              >
                Note: Override results will be considered in the report. SPARK
                scan details below are for reference only.
              </Typography>
            </Box>
          ) : null}

          <Collapse in={!control.override || actualResultOpen}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 1.25,
                mb: 2,
              }}
            >
              {(
                [
                  {
                    label: "Compliance",
                    value: (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                        }}
                      >
                        <ComplianceRing
                          value={compliancePct}
                          size={36}
                          showLabel={false}
                        />
                        <Typography
                          sx={{
                            fontSize: 26,
                            fontWeight: 800,
                            lineHeight: 1,
                            color: isDark ? "#E5E7EB" : "#1E293B",
                          }}
                        >
                          {compliancePct}%
                        </Typography>
                      </Box>
                    ),
                    sub: `${complianceStats.pass} of ${complianceStats.total} passing`,
                  },
                  {
                    label: "Recommendations",
                    value: (
                      <Typography
                        sx={{
                          fontSize: 26,
                          fontWeight: 800,
                          lineHeight: 1,
                          color: isDark ? "#E5E7EB" : "#1E293B",
                        }}
                      >
                        {recommendationStats.total}
                      </Typography>
                    ),
                    sub: `${recommendationStats.compliant} compliant · ${recommendationStats.needAction} need action`,
                  },
                  {
                    label: "Assets",
                    value: (
                      <Typography
                        sx={{
                          fontSize: 26,
                          fontWeight: 800,
                          lineHeight: 1,
                          color: isDark ? "#E5E7EB" : "#1E293B",
                        }}
                      >
                        {assetRows.length}
                      </Typography>
                    ),
                    sub: `${assetStats.compliant} compliant · ${assetStats.needAction} need action`,
                  },
                ] as const
              ).map((card) => (
                <Box
                  key={card.label}
                  sx={{
                    px: 1.75,
                    py: 1.5,
                    borderRadius: 2.5,
                    bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
                    border: `1px solid ${
                      isDark ? "rgba(255,255,255,0.1)" : "#E8ECF4"
                    }`,
                    boxShadow: isDark
                      ? "none"
                      : "0 1px 2px rgba(15, 23, 42, 0.04)",
                    minHeight: 108,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: isDark ? "rgba(255,255,255,0.55)" : "#64748B",
                    }}
                  >
                    {card.label}
                  </Typography>
                  {card.value}
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: muted,
                      mt: "auto",
                    }}
                  >
                    {card.sub}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{
                mb: 1,
                borderTop: `1px solid ${border}`,
                "& .MuiTab-root": {
                  fontSize: "13px",
                  fontWeight: 600,
                  minHeight: 44,
                },
              }}
            >
              <Tab label="Recommendations" />
              <Tab label="Assets" />
            </Tabs>

            {tab === 0 && (
              <Box sx={{ px: 1, pb: 2 }}>
                {recommendationRows.length === 0 ? (
                  <Typography
                    sx={{ p: 2, color: "text.secondary", fontSize: 14 }}
                  >
                    No scanned recommendations for this control yet.
                  </Typography>
                ) : (
                  <AgGridTable
                    columnDefs={recommendationCols}
                    data={recommendationRows}
                    height="calc(100vh - 420px)"
                    showSearch
                    searchPlaceholder="Search recommendations…"
                    pagination
                    paginationPageSize={20}
                    showAppsIcon
                    styles={headerStyles}
                  />
                )}
              </Box>
            )}

            {tab === 1 && (
              <Box sx={{ px: 1, pb: 2 }}>
                {assetRows.length === 0 ? (
                  <Typography
                    sx={{ p: 2, color: "text.secondary", fontSize: 14 }}
                  >
                    No assets with scanned results for this control yet.
                  </Typography>
                ) : (
                  <AgGridTable
                    columnDefs={assetCols}
                    data={assetRows}
                    height="calc(100vh - 420px)"
                    showSearch
                    searchPlaceholder="Search assets…"
                    pagination
                    paginationPageSize={20}
                    showAppsIcon
                    styles={headerStyles}
                  />
                )}
              </Box>
            )}
          </Collapse>
        </Box>
      </Box>
    </Drawer>
  );
};

export default DoTPidDrilldownDrawer;
